import { __rest } from 'tslib';
import { useRouter as useRouter$1 } from 'next/compat/router';
import { useRouter as useRouter$2, usePathname, useSearchParams } from 'next/navigation';
import { useTransition, useEffect } from 'react';

const createRouteObserver = () => {
    let observer = null;
    const setObserver = (callback) => {
        observer = callback;
    };
    const notify = () => {
        if (observer) {
            observer();
        }
    };
    return { setObserver, notify };
};
const routeObserver = createRouteObserver();
const usePagesRouter = () => {
    const pagesRouter = useRouter$1();
    if (!pagesRouter) {
        return null;
    }
    const { locale: lng } = pagesRouter, router = __rest(pagesRouter, ["locale"]);
    const locale = lng === 'en' ? 'en' : 'ar';
    const isLoadingRouter = false;
    return Object.assign(Object.assign({}, router), { locale, isLoadingRouter });
};
const useAppRouter = () => {
    const appRouter = useRouter$2();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const query = Object.assign({}, Object.fromEntries(searchParams.entries()));
    const [isPending, startTransition] = useTransition();
    const segments = pathname.split('/');
    const locale = segments[1] === 'ar' ? 'ar' : 'en';
    const replace = async (url, asPath, options) => {
        if (options === null || options === void 0 ? void 0 : options.shallow) {
            window.history.replaceState(window.history.state, '', url);
            return;
        }
        return new Promise((resolve) => {
            startTransition(() => {
                appRouter.replace(url);
            });
            routeObserver.setObserver(() => {
                resolve();
            });
        });
    };
    const push = async (url, asPath, options) => {
        if (options === null || options === void 0 ? void 0 : options.shallow) {
            window.history.pushState(window.history.state, '', url);
            return;
        }
        return new Promise((resolve) => {
            startTransition(() => {
                appRouter.push(url);
            });
            routeObserver.setObserver(() => {
                resolve();
            });
        });
    };
    useEffect(() => {
        if (!isPending) {
            routeObserver.notify();
        }
    }, [isPending]);
    const router = Object.assign(Object.assign({}, appRouter), { replace,
        push,
        pathname,
        query,
        locale, asPath: '' });
    return router;
};
function useRouter(r) {
    const pagesRouter = usePagesRouter();
    const appRouter = useAppRouter();
    const app = () => {
        if (!appRouter) {
            throw new Error('App router not mounted');
        }
        return appRouter;
    };
    const pages = () => {
        if (!pagesRouter) {
            throw new Error('Pages router not mounted');
        }
        return pagesRouter;
    };
    if (r === 'app') {
        return app();
    }
    if (r === 'pages') {
        return pages();
    }
    if (pagesRouter) {
        return pages();
    }
    else {
        return app();
    }
}

export { useAppRouter, usePagesRouter, useRouter };
